import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  KALEIDESCAPE_TOP_UP_PRESETS_USD,
  formatTopUpFormulaLine,
  topUpCustomerStatusMessage,
  topUpStatusLabel,
  type KaleidescapeTopUpStatus,
} from "@shared/kaleidescapeTopUp";
import { buildPayNowUenFixedAmountPayload } from "@shared/payNowSgqr";
import { Loader2 } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exchangeRate: number;
  adminFeePercent: number;
  onComplete: () => void;
};

type Step = "amount" | "confirm" | "payment";

function formatUsd(n: number): string {
  return `USD ${n.toFixed(2)}`;
}

function formatSgd(n: string | number): string {
  const v = typeof n === "string" ? parseFloat(n) : n;
  return `SGD ${Number.isFinite(v) ? v.toFixed(2) : "0.00"}`;
}

export default function TopUpWalletFlow({
  open,
  onOpenChange,
  exchangeRate,
  adminFeePercent,
  onComplete,
}: Props) {
  const [step, setStep] = useState<Step>("amount");
  const [selectedUsd, setSelectedUsd] = useState<number | null>(100);
  const [customUsd, setCustomUsd] = useState("");
  const [createdRequest, setCreatedRequest] = useState<{
    id: number;
    requestedUsdAmount: string;
    payableSgdAmount: string;
    exchangeRateUsdToSgd: string;
    adminFeePercent: string;
    status: KaleidescapeTopUpStatus;
    payNowReference: string;
    paymentInstructions: {
      payNowUen: string;
      payNowEntityName: string;
      instructionsText: string;
      bankTransferDetails?: string;
    };
  } | null>(null);

  const usdAmount = useMemo(() => {
    if (selectedUsd != null) return selectedUsd;
    const n = parseFloat(customUsd);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [selectedUsd, customUsd]);

  const preview = trpc.kaleidescapeWallet.customerGetTopUpPreview.useQuery(
    { usdAmount },
    { enabled: open && usdAmount > 0 && step === "confirm" },
  );

  const createRequest = trpc.kaleidescapeWallet.customerCreateTopUpRequest.useMutation({
    onSuccess: (data) => {
      setCreatedRequest({
        id: data.request.id,
        requestedUsdAmount: data.request.requestedUsdAmount,
        payableSgdAmount: data.request.payableSgdAmount,
        exchangeRateUsdToSgd: data.request.exchangeRateUsdToSgd,
        adminFeePercent: data.request.adminFeePercent,
        status: data.request.status,
        payNowReference: data.payNowReference,
        paymentInstructions: data.paymentInstructions,
      });
      setStep("payment");
      onComplete();
      toast.success("Top-up request created");
    },
    onError: (err) => toast.error(err.message),
  });

  const payableSgd = preview.data?.payableSgd ?? 0;
  const formulaLine =
    usdAmount > 0
      ? formatTopUpFormulaLine(usdAmount, exchangeRate, adminFeePercent, payableSgd)
      : "";

  const payNowQr =
    createdRequest && parseFloat(createdRequest.payableSgdAmount) > 0
      ? buildPayNowUenFixedAmountPayload({
          uen: createdRequest.paymentInstructions.payNowUen,
          amountSgd: parseFloat(createdRequest.payableSgdAmount),
          billNumber: createdRequest.payNowReference,
          merchantName: createdRequest.paymentInstructions.payNowEntityName,
        })
      : null;

  const reset = () => {
    setStep("amount");
    setSelectedUsd(100);
    setCustomUsd("");
    setCreatedRequest(null);
  };

  const handleClose = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const confirmationMessage = topUpCustomerStatusMessage("pending_payment");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1A2744] font-light tracking-tight">
            {step === "payment" ? "Top-Up Payment Request" : "Top Up Wallet"}
          </DialogTitle>
        </DialogHeader>

        {step === "amount" && (
          <div className="space-y-5">
            <p className="text-sm text-[#8B7355] leading-relaxed">
              Select a top-up amount in USD. Your payable amount in SGD includes the exchange rate and a{" "}
              {adminFeePercent}% administration fee.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {KALEIDESCAPE_TOP_UP_PRESETS_USD.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => {
                    setSelectedUsd(amt);
                    setCustomUsd("");
                  }}
                  className={`rounded-xl border py-3 text-sm font-medium transition-colors ${
                    selectedUsd === amt
                      ? "border-[#1A2744] bg-[#1A2744] text-white"
                      : "border-[#E8E4DC] bg-white text-[#1A2744] hover:border-[#B8956E]"
                  }`}
                >
                  {formatUsd(amt)}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-[#1A2744]">Custom amount (USD)</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                placeholder="e.g. 150"
                value={customUsd}
                onChange={(e) => {
                  setCustomUsd(e.target.value);
                  setSelectedUsd(null);
                }}
                className="border-[#E8E4DC]"
              />
            </div>
            <Button
              className="w-full bg-[#1A2744] hover:bg-[#243556]"
              disabled={usdAmount <= 0}
              onClick={() => setStep("confirm")}
            >
              Continue
            </Button>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-5">
            <div className="rounded-xl border border-[#E8E4DC] bg-[#FAFAF7] p-4 space-y-3 text-sm">
              <p className="text-[11px] tracking-[0.15em] uppercase text-[#B8956E]">Pricing formula</p>
              <p className="text-[#1A2744]">
                SGD Payable = USD Amount × Exchange Rate × {(1 + adminFeePercent / 100).toFixed(2)}
              </p>
              <ul className="text-[#8B7355] text-xs space-y-1 list-disc pl-4">
                <li>Exchange rate converts USD wallet credit into SGD.</li>
                <li>{adminFeePercent}% admin fee covers handling, card processing, and administration.</li>
              </ul>
              <div className="border-t border-[#E8E4DC] pt-3 space-y-1">
                <div className="flex justify-between">
                  <span className="text-[#8B7355]">USD Amount</span>
                  <span className="text-[#1A2744]">{formatUsd(usdAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B7355]">Exchange Rate</span>
                  <span className="text-[#1A2744]">{exchangeRate.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B7355]">Admin Fee</span>
                  <span className="text-[#1A2744]">{adminFeePercent}%</span>
                </div>
                <div className="flex justify-between font-medium pt-1">
                  <span className="text-[#1A2744]">Total Payable (SGD)</span>
                  <span className="text-[#1A2744]">
                    {preview.isLoading ? "…" : formatSgd(payableSgd)}
                  </span>
                </div>
              </div>
              {formulaLine && (
                <p className="text-xs text-[#8B7355] font-mono pt-1">{formulaLine}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep("amount")}>
                Back
              </Button>
              <Button
                className="flex-1 bg-[#1A2744] hover:bg-[#243556]"
                disabled={createRequest.isPending || preview.isLoading || payableSgd <= 0}
                onClick={() => createRequest.mutate({ usdAmount })}
              >
                {createRequest.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating…
                  </>
                ) : (
                  "Confirm Top-Up Request"
                )}
              </Button>
            </div>
          </div>
        )}

        {step === "payment" && createdRequest && (
          <div className="space-y-5">
            <div className="rounded-xl border border-[#E8E4DC] bg-emerald-50/50 p-4 space-y-2 text-sm">
              <p className="font-medium text-[#1A2744]">{confirmationMessage}</p>
              <div className="flex justify-between">
                <span className="text-[#8B7355]">Status</span>
                <span>{topUpStatusLabel(createdRequest.status)}</span>
              </div>
            </div>

            <div className="rounded-xl border border-[#E8E4DC] bg-[#FAFAF7] p-4 space-y-2 text-sm">
              <p className="text-[11px] tracking-[0.15em] uppercase text-[#B8956E]">Payment request</p>
              <div className="flex justify-between">
                <span className="text-[#8B7355]">Customer top-up (USD)</span>
                <span>{formatUsd(parseFloat(createdRequest.requestedUsdAmount))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8B7355]">Exchange rate</span>
                <span>{parseFloat(createdRequest.exchangeRateUsdToSgd).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8B7355]">Admin fee</span>
                <span>{createdRequest.adminFeePercent}%</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-[#1A2744]">Total SGD payable</span>
                <span>{formatSgd(createdRequest.payableSgdAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8B7355]">Reference</span>
                <span className="font-mono text-xs">{createdRequest.payNowReference}</span>
              </div>
            </div>

            {payNowQr && (
              <div className="flex flex-col items-center gap-3 rounded-xl border border-[#E8E4DC] p-4">
                <p className="text-[11px] tracking-[0.15em] uppercase text-[#B8956E]">PayNow QR</p>
                <QRCodeSVG value={payNowQr} size={140} level="H" marginSize={2} />
                <div className="text-xs text-[#8B7355] text-center space-y-1">
                  <p>
                    UEN: <span className="text-[#1A2744]">{createdRequest.paymentInstructions.payNowUen}</span>
                  </p>
                  <p>
                    Reference: <span className="text-[#1A2744]">{createdRequest.payNowReference}</span>
                  </p>
                  <p>
                    Amount:{" "}
                    <span className="text-[#1A2744] font-medium">{formatSgd(createdRequest.payableSgdAmount)}</span>
                  </p>
                </div>
              </div>
            )}

            <p className="text-xs text-[#8B7355] leading-relaxed whitespace-pre-wrap">
              {createdRequest.paymentInstructions.instructionsText}
              {createdRequest.paymentInstructions.bankTransferDetails
                ? `\n\n${createdRequest.paymentInstructions.bankTransferDetails}`
                : ""}
            </p>

            <Button variant="outline" className="w-full" onClick={() => handleClose(false)}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
